import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, User, LogOut } from 'lucide-react';

const Navbar = ({ user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isAdmin = user && user.role === 'admin';
  const navItems = isAdmin ? [
    { path: '/', label: 'Inicio' },
    { path: '/admin', label: 'Admin', protected: true },
    { path: '/dashboard', label: 'Dashboard', protected: true },
  ] : [
    { path: '/', label: 'Inicio' },
    { path: '/validaciones', label: 'Validaciones' },
    { path: '/dashboard', label: 'Dashboard', protected: true },
    { path: '/contacto', label: 'Contacto' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-gray-900 text-white sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_validainfo/artifacts/hme4uwyk_NIKA.png" 
              alt="NIKA Logo" 
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold">NIKA</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => {
              if (item.protected && !user) return null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-blue-400 ${
                    isActive(item.path) ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-300'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* User Menu / Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <User size={20} />
                  <span className="text-sm">{user.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLogout}
                  className="text-gray-300 hover:text-white"
                >
                  <LogOut size={16} className="mr-2" />
                  Salir
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white" data-testid="navbar-login-btn">
                    Iniciar Sesión
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-gray-800 pb-4">
            <div className="flex flex-col space-y-3 px-4 pt-4">
              {navItems.map((item) => {
                if (item.protected && !user) return null;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`text-sm font-medium py-2 transition-colors ${
                      isActive(item.path) ? 'text-blue-400' : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              
              {user ? (
                <div className="pt-3 border-t border-gray-700">
                  <div className="flex items-center space-x-2 mb-3">
                    <User size={16} />
                    <span className="text-sm">{user.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onLogout();
                      setIsMenuOpen(false);
                    }}
                    className="text-gray-300 hover:text-white w-full justify-start"
                  >
                    <LogOut size={16} className="mr-2" />
                    Salir
                  </Button>
                </div>
              ) : (
                <div className="pt-3 border-t border-gray-700 space-y-2">
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full justify-start text-gray-300" data-testid="navbar-mobile-login-btn">
                      Iniciar Sesión
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;